'use client';

import { Fragment } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { Chip } from '@packages/shared/src/ui/Chip/Chip';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { unmask } from '@packages/gql/src/betterMasking';
import type { SelectedFilterValueListV2FragmentFragmentDoc } from '@packages/gql/generated/shopping/SelectedFilterValueListV2FragmentFragmentDoc';

import { SPECIAL_CATEGORY_FILTER_ID } from '../../hooks/useSpecialCategoryFilterValue';
import { useSearchFilters } from '../../hooks/useFilters';
import { SelectedFilterRangeChipV2 } from '../SelectedFilterRangeChipV2/SelectedFilterRangeChipV2';
import { SelectedFilterValueChipV2 } from '../SelectedFilterValueChipV2/SelectedFilterValueChipV2';

const messages = defineMessages({
  deleteAllLabel: {
    id: 'filtersV2.selectedValues.deleteAll',
    defaultMessage: 'Alle Filter löschen',
  },
  chipsContainerLabel: {
    id: 'filters.chips.container.label',
    defaultMessage: 'Ausgewählte Filter',
  },
});

/* GraphQL */ `
  fragment SelectedFilterValueListV2Fragment on SearchFilterableAndSortableResult {
    ...UseSearchFiltersFragment
    activeFilters {
      filterId
      activeValues {
        id
        ...SelectedFilterValueChipV2Fragment
      }
    }

    ... on SuccessfulSearchResult {
      selectedCategory {
        id
        displayName
      }
    }
  }
`;

export type SelectedFilterValueListV2Props = {
  maskedData: FragmentType<typeof SelectedFilterValueListV2FragmentFragmentDoc>;
  /** If `true`, the color of the filter value is displayed on the chip */
  showColors?: boolean;
  onRemoveFilter?: () => void;
};

/**
 * A list of currently selected filter values
 * */
export const SelectedFilterValueListV2 = ({
  maskedData,
  showColors,
  onRemoveFilter,
}: SelectedFilterValueListV2Props) => {
  const { formatMessage } = useIntl();

  const data = unmask<typeof SelectedFilterValueListV2FragmentFragmentDoc>(maskedData);

  const { selectedFilterValues, pushFilterChange } = useSearchFilters(data);

  const removeFilterValue = (filterId: string, valueId: string) => {
    const otherValues = selectedFilterValues.filter((value) => value.filterId !== filterId);
    const relevantValue = selectedFilterValues.find((value) => value.filterId === filterId);

    if (!relevantValue) return;

    const { selectedValues, selectedRange } = relevantValue;

    pushFilterChange(
      otherValues.concat({
        filterId,
        selectedRange,
        selectedValues: selectedValues.filter((id) => id !== valueId),
      }),
    );
    onRemoveFilter?.();
  };

  const removeAllFilters = () => {
    pushFilterChange([]);
    onRemoveFilter?.();
  };

  const hasSelectedCategory = 'selectedCategory' in data && !!data.selectedCategory;
  const hasSelectedFilters =
    hasSelectedCategory ||
    selectedFilterValues.some(
      ({ selectedValues, selectedRange }) => selectedValues.length > 0 || !!selectedRange,
    );

  const removeRangeValue = (filterId: string) => {
    const otherValues = selectedFilterValues.filter((value) => value.filterId !== filterId);
    const relevantValue = selectedFilterValues.find((value) => value.filterId === filterId);

    if (!relevantValue) return;

    const { selectedValues } = relevantValue;

    pushFilterChange(otherValues.concat({ filterId, selectedValues }));
    onRemoveFilter?.();
  };

  const getValueById = (filterId: string, valueId: string) =>
    data.activeFilters
      .find((f) => f.filterId === filterId)
      ?.activeValues?.find((v) => v.id === valueId);

  return (
    <div className="mt-1 flex w-full flex-col flex-wrap gap-1 md:flex-row">
      <ul
        className="mr-auto flex w-full flex-1 flex-wrap gap-1"
        aria-label={formatMessage(messages.chipsContainerLabel)}
      >
        {'selectedCategory' in data && data.selectedCategory && (
          <li>
            <Chip
              onDelete={
                () => removeFilterValue(SPECIAL_CATEGORY_FILTER_ID, data.selectedCategory?.id ?? '') // TS can't correctly infer here that `selectedCategory` is defined
              }
              onClick={
                () => removeFilterValue(SPECIAL_CATEGORY_FILTER_ID, data.selectedCategory?.id ?? '') // TS can't correctly infer here that `selectedCategory` is defined
              }
            >
              {data.selectedCategory.displayName}
            </Chip>
          </li>
        )}
        {selectedFilterValues.flatMap(({ filterId, selectedValues, selectedRange }) => (
          <Fragment key={filterId}>
            {selectedRange && (
              <li key={`${filterId}-range`}>
                <SelectedFilterRangeChipV2
                  value={selectedRange}
                  onDelete={() => removeRangeValue(filterId)}
                />
              </li>
            )}
            {selectedValues
              .map((id) => getValueById(filterId, id))
              .map((value) =>
                value ? (
                  <li key={`${filterId}-${value.id}`}>
                    <SelectedFilterValueChipV2
                      key={`${filterId}-${value.id}`}
                      onDelete={() => removeFilterValue(filterId, value.id)}
                      maskedData={value}
                      showColors={showColors}
                    />
                  </li>
                ) : null,
              )}
          </Fragment>
        ))}
        {hasSelectedFilters && (
          <li>
            <Chip
              variant="filled"
              label={formatMessage(messages.deleteAllLabel)}
              onClick={removeAllFilters}
              onDelete={removeAllFilters}
              ariaLabelForDelete={formatMessage(messages.deleteAllLabel)}
            />
          </li>
        )}
      </ul>
    </div>
  );
};
